import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../complaints/ComplaintManagement.css';

const AddExpenseCategory = ({ category, onClose }) => {
  const isEditMode = category && category.id;

  const [form, setForm] = useState({
    name: '',
    is_staff_expense: false,
    status: 'ACTIVE',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        name: category.name || '',
        is_staff_expense: Boolean(category.is_staff_expense),
        status: category.status || 'ACTIVE',
      });
    } else {
      setForm({
        name: '',
        is_staff_expense: false,
        status: 'ACTIVE',
      });
    }
  }, [category, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    if (!form.status) {
      newErrors.status = 'Status is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const body = {
        name: form.name.trim(),
        is_staff_expense: Boolean(form.is_staff_expense),
        status: form.status,
      };

      if (isEditMode) {
        await apiClient.post(`/expenses/categories/${category.id}/update`, body);
        toastSuccess('Expense category updated successfully');
      } else {
        await apiClient.post('/expenses/categories', body);
        toastSuccess('Expense category created successfully');
      }
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to save expense category';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ background: 'transparent' }}>
        <h2 className="rt-title">
          {isEditMode ? 'Edit Expense Category' : 'Add Expense Category'}
        </h2>
      </div>
      <div className="rt-main-section">
        <form onSubmit={handleSubmit} className="complaint-form">
          <div className="complaint-form-sections">
            <div className="complaint-section">
              <h3 className="complaint-section-title">Expense Category Details</h3>

              <div className="complaint-form-group">
                <label>
                  Category Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Groceries, Salary, Maintenance"
                  required
                  disabled={loading}
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              <div className="complaint-form-group">
                <label>Staff expense</label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontWeight: 400,
                    marginBottom: 0,
                    cursor: loading ? 'default' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_staff_expense"
                    checked={form.is_staff_expense}
                    onChange={handleChange}
                    disabled={loading}
                    style={{
                      width: '16px',
                      height: '16px',
                      margin: 0,
                      padding: 0,
                      flexShrink: 0,
                      accentColor: '#153C7F',
                    }}
                  />
                  <span>Require selecting a staff member when recording</span>
                </label>
              </div>

              <div className="complaint-form-group">
                <label>
                  Status <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
                {errors.status && <span className="form-error">{errors.status}</span>}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'center' }}>
            <RTButton type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Category' : 'Add Category'}
            </RTButton>
            <RTButton type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </RTButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpenseCategory;
